import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { AdminPanel } from "./components/admin/AdminPanel";

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("root element missing");

// AdminPanel is a static import (not lazy) on purpose: when the bundle is
// embedded in a Shopify theme, assets live under /cdn/shop/t/<n>/assets/ —
// a path that changes every theme push. A lazy chunk's import URL is fixed
// at build time and resolves to /assets/... which 404s on the storefront.
// Bundling AdminPanel in (~8KB gzip) sidesteps the whole chunk-path problem.

// Routing: the admin panel renders when the URL asks for it, everything
// else renders the main storefront app.
//
//   - Local dev (Vite):       /admin
//   - Shopify storefront:     /pages/monogatari-admin (or any /pages/<handle>
//                             where the handle ends in "-admin" or equals
//                             "admin"). Shopify reserves the bare /admin
//                             path for its own backend, so a Shopify Page
//                             must live under /pages/<handle>.
//   - Escape hatch (anywhere): ?admin query param — useful in dev, on
//                              preview URLs, or to flip the homepage into
//                              admin temporarily.
//
// Admin still requires the Bearer-token form below. URL detection only
// controls which component renders, not whether the API will trust the
// caller.
function isAdminUrl(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname;
  if (path === "/admin" || path === "/admin/") return true;
  // Match /pages/<handle> where handle is "admin" or ends in "-admin".
  // Stops a URL like /products/admin-only-thing from accidentally tripping
  // admin mode.
  if (/^\/pages\/(admin|.+-admin)\/?$/.test(path)) return true;
  return new URLSearchParams(window.location.search).has("admin");
}

function Root() {
  return isAdminUrl() ? <AdminPanel /> : <App />;
}

createRoot(rootEl).render(
  <StrictMode>
    <Root />
  </StrictMode>
);
